// src/styles/colors.ts

// Basic color palette definitions

// --- Light Theme ---
export const lightColors = {
  background: "#f8f9fa",
  textPrimary: "#212529",
  textSecondary: "#6c757d",
  grid: "#dee2e6",
  axis: "#495057",
  tooltipBackground: "rgba(255, 255, 255, 0.9)",
  tooltipText: "#212529",
  // Define other colors as needed
};

// --- Dark Theme ---
export const darkColors = {
  background: "#343a40",
  textPrimary: "#f8f9fa",
  textSecondary: "#adb5bd",
  grid: "#495057",
  axis: "#ced4da",
  tooltipBackground: "rgba(33, 37, 41, 0.9)",
  tooltipText: "#f8f9fa",
  // Define other colors as needed
};

// Default series colors as fallback
export const defaultSeriesColors = [
  "#2196f3",
  "#4caf50",
  "#ff9800",
  "#e91e63",
  "#9c27b0",
  "#795548",
  "#607d8b",
];

/**
 * Get chart colors for series, prioritizing company colors
 * @param companyColors - Company chart colors from API
 * @returns Array of colors for chart series
 */
export function getChartSeriesColors(companyColors: string[] = []): string[] {
  return companyColors.length > 0 ? companyColors : defaultSeriesColors;
}

/**
 * Get a specific color from the chart series colors
 * @param index - Index of the color to get
 * @param companyColors - Company chart colors from API
 * @returns Hex color string
 */
export function getChartColorByIndex(index: number, companyColors: string[] = []): string {
  const colors = getChartSeriesColors(companyColors);
  return colors[index % colors.length];
}

/**
 * Generate a color palette with tints for charts
 * @param baseColor - Base color hex string
 * @param tints - Optional predefined tint values
 * @returns Object with tint variations
 */
export function generateColorTints(
  baseColor: string,
  tints?: { "100": string; "60": string; "30": string; "15": string }
): { "100": string; "60": string; "30": string; "15": string } {
  if (tints) {
    return tints;
  }

  // Generate tints programmatically if not provided
  // This is a simple approach - you might want a more sophisticated color manipulation library
  const hexToRgb = (hex: string) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null;
  };

  const rgbToHex = (r: number, g: number, b: number) => {
    return "#" + [r, g, b].map(x => {
      const hex = Math.round(x).toString(16);
      return hex.length === 1 ? "0" + hex : hex;
    }).join("");
  };

  const rgb = hexToRgb(baseColor);
  if (!rgb) return { "100": baseColor, "60": baseColor, "30": baseColor, "15": baseColor };

  const white = { r: 255, g: 255, b: 255 };

  return {
    "100": baseColor,
    "60": rgbToHex(
      rgb.r + (white.r - rgb.r) * 0.4,
      rgb.g + (white.g - rgb.g) * 0.4,
      rgb.b + (white.b - rgb.b) * 0.4
    ),
    "30": rgbToHex(
      rgb.r + (white.r - rgb.r) * 0.7,
      rgb.g + (white.g - rgb.g) * 0.7,
      rgb.b + (white.b - rgb.b) * 0.7
    ),
    "15": rgbToHex(
      rgb.r + (white.r - rgb.r) * 0.85,
      rgb.g + (white.g - rgb.g) * 0.85,
      rgb.b + (white.b - rgb.b) * 0.85
    ),
  };
}
